/**
 * Koordinator Signup Page — Passwordless email + Google SSO
 * No passwords. Redirected to from landing page CTA.
 */
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Input, Card, CardContent, CardHeader, CardTitle, CardDescription } from '@bright-web-skeleton/ui';
import { VerifyEmailScreen } from '@bright-web-skeleton/auth';
import { useAuth } from '../../shared/contexts';
import { ArrowRight, Mail, Building2, Loader2 } from 'lucide-react';
import { FcGoogle } from 'react-icons/fc';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';

export function SignupPage() {
  const navigate = useNavigate();
  const { signInWithGoogle } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [role, setRole] = useState<'brand_manager' | 'brand_admin'>('brand_manager');

  const handleRoleChange = (r: 'brand_manager' | 'brand_admin') => setRole(r);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error('Please enter your email address');
      return;
    }
    setIsLoading(true);

    try {
      localStorage.setItem('koordinator_signup_role', role);
      
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          shouldCreateUser: true,
        },
      });
      if (error) {
        toast.error(error.message);
      } else {
        try { localStorage.setItem('bright_last_user_email', email); } catch { /* noop */ }
        setPendingEmail(email);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to send sign-in link.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignUp = async () => {
    setGoogleLoading(true);
    try {
      localStorage.setItem('koordinator_signup_role', role);
      await signInWithGoogle();
    } catch (err: any) {
      toast.error(err.message || 'Google sign up failed.');
      setGoogleLoading(false);
    }
  };

  if (pendingEmail) {
    return (
      <VerifyEmailScreen
        appName="Kloutsale"
        pendingEmail={pendingEmail}
        onBack={() => setPendingEmail(null)}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center space-x-2 mb-8">
          <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
            <span className="text-primary-foreground font-bold">K</span>
          </div>
          <span className="font-bold text-2xl">Kloutsale®</span>
        </div>

        <Card className="border-0 shadow-xl">
          <CardHeader className="text-center pb-4">
            <CardTitle className="text-2xl">Create your account</CardTitle>
            <CardDescription>
              Start your free trial — no credit card required
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            {/* Google SSO */}
            <Button
              type="button"
              variant="outline"
              className="w-full min-h-[48px] mb-4 flex items-center justify-center gap-2 border-border hover:bg-muted"
              onClick={handleGoogleSignUp}
              disabled={googleLoading || isLoading}
            >
              {googleLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <FcGoogle className="w-5 h-5" />
              )}
              {googleLoading ? 'Signing up...' : 'Continue with Google'}
            </Button>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-card px-2 text-muted-foreground">or continue with email</span>
              </div>
            </div>

            <form onSubmit={handleEmailSignIn} className="space-y-4">
              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium mb-1.5 text-foreground">
                  Email
                </label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="min-h-[48px]"
                  disabled={isLoading || googleLoading}
                />
              </div>

              {/* Role Selection */}
              <div>
                <label className="block text-sm font-medium mb-2 text-foreground">Your role</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleRoleChange('brand_manager')}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      role === 'brand_manager'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-muted-foreground'
                    }`}
                    disabled={isLoading || googleLoading}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 className="w-4 h-4 text-primary" />
                      <span className="font-medium text-sm">Brand Manager</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Manage campaigns</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRoleChange('brand_admin')}
                    className={`p-3 rounded-lg border-2 transition-all text-left ${
                      role === 'brand_admin'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:border-muted-foreground'
                    }`}
                    disabled={isLoading || googleLoading}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 className="w-4 h-4 text-primary" />
                      <span className="font-medium text-sm">Brand Admin</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Full control</p>
                  </button>
                </div>
              </div>

              <Button 
                type="submit" 
                className="w-full min-h-[48px] mt-2" 
                disabled={isLoading || googleLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Mail className="mr-2 h-4 w-4" />
                    Continue with Email
                  </>
                )}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                We'll send you a secure sign-in link. No password needed.
              </p>

              <p className="text-xs text-muted-foreground text-center">
                By continuing you agree to our{' '}
                <Link to="/terms" className="underline hover:text-foreground">Terms</Link>
                {' '}and{' '}
                <Link to="/privacy" className="underline hover:text-foreground">Privacy Policy</Link>.
              </p>
            </form>

            <div className="mt-6 pt-6 border-t text-center space-y-3">
              <p className="text-sm text-muted-foreground">
                Already have an account?{' '}
                <Link to="/login" className="text-primary font-medium hover:underline">
                  Sign in
                </Link>
              </p>
              <Link 
                to="/" 
                className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Back to Landing
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default SignupPage;